import { roleMeta } from '@hrms/shared'
import { useEffect, useId, useRef, useState } from 'react'
import { LuChevronDown, LuChevronRight, LuCompass, LuLogOut, LuPlay } from 'react-icons/lu'
import { useNavigate } from 'react-router'
import { useCurrentUser, useSignOut } from '../auth/AuthProvider.tsx'
import { Avatar } from '../components/ui/Avatar.tsx'
import { Lozenge } from '../components/ui/Lozenge.tsx'
import { useToast } from '../components/ui/Toast.tsx'
import { useHealth } from '../lib/api.ts'
import { cn } from '../lib/cn.ts'
import { hint } from '../lib/hint.ts'
import { useTour } from '../tours/useTour.ts'

function StatusLine() {
  const { data, isPending, isError } = useHealth()
  const state = isPending ? 'checking' : !isError && data?.db ? 'live' : 'down'
  const text = {
    checking: 'Checking connection…',
    live: 'Connected',
    down: 'Server or database unavailable',
  }[state]
  const color = { checking: 'var(--faint)', live: 'var(--sync-live)', down: 'var(--sync-local)' }[
    state
  ]
  return (
    <p className="flex items-center gap-2 px-3 py-2 text-xs text-muted">
      <span aria-hidden className="size-[7px] rounded-full" style={{ background: color }} />
      {text}
    </p>
  )
}

export function UserMenu() {
  const user = useCurrentUser()
  const signOut = useSignOut()
  const navigate = useNavigate()
  const toast = useToast()
  const { start, available } = useTour()
  const [open, setOpen] = useState(false)
  const [toursOpen, setToursOpen] = useState(false)
  const toursId = useId()
  const tours = open ? available() : []
  const [signingOut, setSigningOut] = useState(false)
  const role = roleMeta(user)

  const onSignOut = async () => {
    setSigningOut(true)
    try {
      await signOut()
      navigate('/login', { replace: true })
    } catch {
      toast('Couldn’t sign out. Check your connection and try again.')
      setSigningOut(false)
    }
  }
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) setToursOpen(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      trigger.current?.focus()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const item =
    'flex w-full items-center gap-2.5 rounded-ctl border-0 bg-transparent px-3 py-2 text-left text-sm text-text hover:bg-surface-2 disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-transparent'

  return (
    <div ref={root} className="relative" data-tour="user-menu">
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account: ${user.name}`}
        onClick={() => setOpen((o) => !o)}
        className="flex h-8.5 items-center gap-2 rounded-full border border-border bg-surface-2 py-0 pr-2 pl-1 text-text hover:border-border-strong"
      >
        <Avatar name={user.name} seed={user.email} src={user.avatarUrl} />
        <span className="max-w-[160px] truncate text-[13px] font-medium max-[720px]:hidden">
          {user.name}
        </span>
        <LuChevronDown
          aria-hidden
          className={cn('text-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          id={menuId}
          className="absolute top-[calc(100%+6px)] right-0 z-50 w-[min(280px,calc(100vw-24px))] animate-fade rounded-panel border border-border bg-surface p-1.5 shadow-pop"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <Avatar name={user.name} seed={user.email} src={user.avatarUrl} size="lg" />
            <div className="min-w-0">
              <p className="truncate font-semibold">{user.name}</p>
              <p className="truncate text-[12.5px] text-muted">{user.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 px-3 pb-2 text-xs text-muted">
            <span className="inline-flex" {...hint(role.description, 'left')}>
              <Lozenge color={role.color}>{role.label}</Lozenge>
              <span className="sr-only">. {role.description}</span>
            </span>
            {user.dept && <span className="truncate">{user.dept}</span>}
          </div>
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            className={item}
            aria-expanded={toursOpen}
            aria-controls={toursId}
            onClick={() => setToursOpen((o) => !o)}
          >
            <LuCompass aria-hidden className="text-base text-violet-fg" />
            <span className="flex-1">Take a tour</span>
            <LuChevronRight
              aria-hidden
              className={cn('text-muted transition-transform', toursOpen && 'rotate-90')}
            />
          </button>
          {toursOpen && (
            <ul id={toursId} aria-label="Tours" className="mb-1 ml-3 border-l border-border pl-1.5">
              {tours.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={cn(item, 'items-start py-1.5')}
                    onClick={() => {
                      setOpen(false)
                      start(t.id)
                    }}
                  >
                    <LuPlay aria-hidden className="mt-1 size-3 shrink-0 text-violet-fg" />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium">{t.title}</span>
                      <span className="block text-[12px] text-muted">{t.summary}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className={item} onClick={onSignOut} disabled={signingOut}>
            <LuLogOut aria-hidden className="text-base text-rose-fg" />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
          <div className="my-1 h-px bg-border" />
          <StatusLine />
        </div>
      )}
    </div>
  )
}
